import { useState, useEffect, type FormEvent } from "react";
import { useRecoilState } from "recoil";
import axios from "axios";
import { Dialog } from "@headlessui/react";
import { toast } from "react-toastify";
import SyncLoader from "components/Loaders/SyncLoader";
import {
  supportTaskModalState,
  isTasksUpdatedState,
} from "store/supportTasks/atoms/supportTasksState";
import { IoMdClose } from "react-icons/io";
import { getToken } from "utils/getToken";
import {
  fieldLabel,
  focusRing,
  input as inputCls,
  primaryBtn,
  select as selectCls,
} from "components/ui/instrument";

const CreateTaskModal = () => {
  const [token, setToken] = useState(getToken());
  const [supportTaskModal, setSupportTaskModal] = useRecoilState(
    supportTaskModalState
  );
  const [isTasksUpdated, setIsTasksUpdated] =
    useRecoilState(isTasksUpdatedState);
  const [loading, setLoading] = useState(false);

  const [formValues, setFormValues] = useState({
    description: supportTaskModal?.taskDetails?.description || "",
    scheduled_at: supportTaskModal?.taskDetails?.scheduled_at || "",
    remarks: "",
    user_name: supportTaskModal?.taskDetails?.user_name || "",
    mobile: supportTaskModal?.taskDetails?.mobile || "",
    status: supportTaskModal?.taskDetails?.status || "",
  });

  const selectedTaskId = supportTaskModal?.taskId;
  const selectedTaskDeatils = supportTaskModal?.taskDetails;
  const tasksRemarkHistory = supportTaskModal?.taskDetails?.remarks || [];
  const taskType = supportTaskModal?.taskDetails?.type;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Token ${token}`,
  };

  const handleUpdateTaskDetails = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formValues.scheduled_at) {
      return toast.error("Enter a scheduled_at time for the task!");
    }

    if (selectedTaskId) {
      try {
        setLoading(true);

        const config = {
          method: "post",
          url: process.env.REACT_APP_API_URL + "tasks/edit/",
          headers: headers,
          data: {
            task_id: selectedTaskId,
            scheduled_at: formValues.scheduled_at,
            user_name: formValues.user_name,
            mobile: formValues.mobile,
            status: formValues.status,
            remarks: formValues.remarks,
          },
        };

        // Update the data on the server using Axios
        const updateTaskResponse = await axios(config);

        if (updateTaskResponse.status === 200) {
          toast.success("Successfully Updated Task!");
          setIsTasksUpdated(true);
          handleCloseModal();
        }
      } catch (error: any) {
        console.error("UPDATE_TASK_DETAILS_ERROR", error);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        } else {
          toast.error("Something went wrong while updating task!");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateTask = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formValues.mobile) {
      try {
        setLoading(true);

        const config = {
          method: "post",
          url: process.env.REACT_APP_API_URL + "tasks/add/",
          headers: headers,
          data: {
            description: formValues.description,
            scheduled_at: formValues.scheduled_at,
            remarks: formValues.remarks,
            type: "custom_task",
            user_name: formValues.user_name,
            mobile: formValues.mobile,
          },
        };

        const createTaskResponse = await axios(config);

        if (createTaskResponse.status === 200) {
          toast.success("Task is created successfully!");
          setIsTasksUpdated(true);
          handleCloseModal();
        }
      } catch (error) {
        console.error("CREATE_SUPPORT_TASK_ERROR", error);
        toast.error("Something went wrong while creating task!");
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("Please enter usermobile.");
    }
  };

  const getEditTaskForm = () => {
    return (
      <div>
        <form onSubmit={handleUpdateTaskDetails} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* <div className="w-full text-sm">
              <p className="my-1.5 text-start font-semibold">
                Task Description
              </p>
              <input
                value={formValues.description}
                required
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      description: e.target.value,
                    };
                  })
                }
                placeholder="Enter a description for this task"
                className={inputCls}
              />
            </div> */}

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>Parent's Mobile</p>
              <input
                readOnly={taskType !== "custom_task"}
                value={formValues.mobile}
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      mobile: e.target.value,
                    };
                  })
                }
                placeholder="Enter user's mobile number"
                className={`tabular-nums ${inputCls}`}
              />
            </div>

            <div className="w-full text-sm">
              <p className={`text-start ${fieldLabel}`}>Status:</p>
              <select
                value={formValues.status}
                onChange={(e) => {
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      status: e.target.value,
                    };
                  });
                }}
                className={`w-full ${selectCls}`}
              >
                <option value={"default"}>Choose the status</option>
                <option value={"done"}>Done</option>
                <option value={"pending"}>Pending</option>
              </select>
            </div>

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>
                Task Due Date:<span className="text-crit-600">*</span>
              </p>

              <input
                type="datetime-local"
                value={formValues.scheduled_at}
                onChange={(e) => {
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      scheduled_at: e.target.value,
                    };
                  });
                }}
                className={`tabular-nums ${inputCls}`}
              />
            </div>

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>Task Remark:</p>
              <input
                value={formValues.remarks}
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      remarks: e.target.value,
                    };
                  })
                }
                placeholder="Enter a remark for this task"
                className={inputCls}
              />
            </div>

            {tasksRemarkHistory.length > 0 && (
              <div className="w-full text-sm">
                <p className={`my-1.5 text-start ${fieldLabel}`}>
                  Remark History:
                </p>

                {tasksRemarkHistory.map((remark: any, index: number) => (
                  <div
                    key={index}
                    className="my-2 w-full rounded-md border border-clinic-200 bg-clinic-50 px-3 py-2 text-clinic-700"
                  >
                    <p className="truncate">{remark.remark}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            {loading ? (
              <div>
                <div className="rounded-md bg-action-600 px-4 py-2 text-white">
                  <SyncLoader size="5px" />
                </div>
              </div>
            ) : (
              <button className={primaryBtn}>
                Update Task
              </button>
            )}
          </div>
        </form>
      </div>
    );
  };

  const getCreateTaskForm = () => {
    return (
      <div>
        <form onSubmit={handleCreateTask} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-6">
            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>
                Task Description:<span className="text-crit-600">*</span>
              </p>
              <input
                value={formValues.description}
                required
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      description: e.target.value,
                    };
                  })
                }
                placeholder="Enter a description for this task"
                className={inputCls}
              />
            </div>

            {/* <div className="w-full text-sm">
              <p className="my-1.5 text-start font-semibold">User Name</p>
              <input
                value={formValues.user_name}
                required
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      user_name: e.target.value,
                    };
                  })
                }
                placeholder="Enter a name for user"
                className={inputCls}
              />
            </div> */}

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>
                Parent's Mobile:
              </p>
              <input
                value={formValues.mobile}
                required
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      mobile: e.target.value,
                    };
                  })
                }
                placeholder="Enter user's mobile number"
                className={`tabular-nums ${inputCls}`}
              />
            </div>

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>
                Task Due Date:<span className="text-crit-600">*</span>
              </p>

              <input
                type="datetime-local"
                value={formValues.scheduled_at}
                required
                onChange={(e) => {
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      scheduled_at: e.target.value,
                    };
                  });
                }}
                className={`tabular-nums ${inputCls}`}
              />
            </div>

            <div className="w-full text-sm">
              <p className={`my-1.5 text-start ${fieldLabel}`}>Task Remark:</p>
              <input
                value={formValues.remarks}
                onChange={(e) =>
                  setFormValues((prev) => {
                    return {
                      ...prev,
                      remarks: e.target.value,
                    };
                  })
                }
                placeholder="Enter a remark for this task"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            {loading ? (
              <div>
                <div className="rounded-md bg-action-600 px-4 py-2 text-white">
                  <SyncLoader size="5px" />
                </div>
              </div>
            ) : (
              <button className={primaryBtn}>
                Create Task
              </button>
            )}
          </div>
        </form>
      </div>
    );
  };

  const handleCloseModal = () => {
    setSupportTaskModal({
      open: false,
      taskDetails: null,
      taskId: null,
      type: null,
    });

    setFormValues({
      description: "",
      scheduled_at: "",
      remarks: "",
      user_name: "",
      mobile: "",
      status: "",
    });
  };

  useEffect(() => {
    if (selectedTaskId) {
      setFormValues({
        description: supportTaskModal?.taskDetails?.description || "",
        scheduled_at: supportTaskModal?.taskDetails?.scheduled_at || "",
        remarks: "",
        user_name: supportTaskModal?.taskDetails?.user_name || "",
        mobile: supportTaskModal?.taskDetails?.mobile || "",
        status: supportTaskModal?.taskDetails?.status || "",
      });
    }
  }, [selectedTaskId]);

  return (
    <Dialog
      open={supportTaskModal.open}
      onClose={() => {
        handleCloseModal();
      }}
      className="relative z-50"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <div className="fixed inset-0 bg-[#000] opacity-20" aria-hidden="true" />

      {/* Full-screen scrollable container */}
      <div className="fixed inset-0 overflow-y-auto">
        {/* Container to center the panel */}
        <div className="flex items-center justify-center min-h-screen p-4">
          {/* The actual dialog panel  */}
          <Dialog.Panel className="mx-auto w-full max-w-md rounded md:min-w-[40%]">
            <div className="my-8 inline-block w-full rounded-lg border border-clinic-200 bg-white p-6 shadow-lg">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    handleCloseModal();
                  }}
                  aria-label="Close"
                  className={`rounded text-clinic-500 hover:text-clinic-700 ${focusRing}`}
                >
                  <IoMdClose size={25} />
                </button>
              </div>
              <Dialog.Title
                as="h3"
                className="text-center text-base font-bold leading-6 text-clinic-900"
              >
                {supportTaskModal.type === "add"
                  ? "Create Task"
                  : "Edit Task Details"}
              </Dialog.Title>
              {supportTaskModal.type
                ? supportTaskModal.type === "add"
                  ? getCreateTaskForm()
                  : getEditTaskForm()
                : null}
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateTaskModal;
